import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { WsProvider } from "./context/WsContext";

import Landing from "./pages/Landing";
import HonoluluFirstDateIdeas from "./pages/HonoluluFirstDateIdeas";
import MeetSinglesHonolulu from "./pages/MeetSinglesHonolulu";
import FirstDateSafetyTips from "./pages/FirstDateSafetyTips";
import MarketLanding from "./pages/MarketLanding";
import Auth from "./pages/Auth";
import Discover from "./pages/Discover";
import ProfileDetail from "./pages/ProfileDetail";
import MyProfile from "./pages/MyProfile";
import Offers from "./pages/Offers";
import Messages from "./pages/Messages";
import Wallet from "./pages/Wallet";
import Earnings from "./pages/Earnings";
import Favorites from "./pages/Favorites";
import Notifications from "./pages/Notifications";
import Verify from "./pages/Verify";
import LegalPage from "./legal/LegalPage";
import SafetyCenter from "./pages/SafetyCenter";
import CompleteRegistration from "./pages/CompleteRegistration";
import PrivacyPolicy from "./legal/PrivacyPolicy";
import { LEGAL_ENABLED } from "./lib/flags";
import HowCredits from "./pages/HowCredits";
import Admin from "./pages/Admin";
import Onboarding from "./pages/Onboarding";
import MyApplication from "./pages/MyApplication";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";
import AppShell from "./components/AppShell";
import { captureAttribution, trackPageview } from "./lib/track";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => { captureAttribution(); }, []);
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);
  return null;
}

function AuthCallback() {
  const { completeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      completeGoogleSession(decodeURIComponent(match[1]))
        .then((res) => {
          window.history.replaceState(null, "", "/");
          if (res && res.otp_required) { navigate("/auth"); }
          else if (res && res.registration_incomplete) { navigate("/complete-registration"); }
          else { navigate("/discover"); }
        })
        .catch(() => { navigate("/auth"); });
    } else navigate("/auth");
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-[#D4AF37] font-serif text-2xl">
      Preparing your evening…
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#9E978E]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.registration_incomplete) return <Navigate to="/complete-registration" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/discover" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/honolulu-first-date-ideas" element={<HonoluluFirstDateIdeas />} />
      <Route path="/meet-singles-honolulu" element={<MeetSinglesHonolulu />} />
      <Route path="/first-date-safety-tips" element={<FirstDateSafetyTips />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/safety" element={<SafetyCenter />} />
      <Route path="/complete-registration" element={<CompleteRegistration />} />
      {LEGAL_ENABLED && <Route path="/legal/:slug" element={<LegalPage />} />}
      <Route path="/market/:marketId" element={<MarketLanding />} />
      <Route path="/discover" element={<Protected><AppShell><Discover /></AppShell></Protected>} />
      <Route path="/profile/:userId" element={<Protected><AppShell><ProfileDetail /></AppShell></Protected>} />
      <Route path="/me" element={<Protected><AppShell><MyProfile /></AppShell></Protected>} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/offers" element={<Protected><AppShell><Offers /></AppShell></Protected>} />
      <Route path="/messages" element={<Protected><AppShell><Messages /></AppShell></Protected>} />
      <Route path="/messages/:connectionId" element={<Protected><AppShell><Messages /></AppShell></Protected>} />
      <Route path="/wallet" element={<Protected><AppShell><Wallet /></AppShell></Protected>} />
      <Route path="/earnings" element={<Protected><AppShell><Earnings /></AppShell></Protected>} />
      <Route path="/credits/how" element={<Protected><AppShell><HowCredits /></AppShell></Protected>} />
      <Route path="/favorites" element={<Protected><AppShell><Favorites /></AppShell></Protected>} />
      <Route path="/notifications" element={<Protected><AppShell><Notifications /></AppShell></Protected>} />
      <Route path="/application" element={<Protected><AppShell><MyApplication /></AppShell></Protected>} />
      <Route path="/verify" element={<Protected><AppShell><Verify /></AppShell></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <WsProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <RouteTracker />
          <AppRouter />
        </BrowserRouter>
      </WsProvider>
    </AuthProvider>
  );
}
